/**
 * The single shared Firebase app instance, used by both Analytics (`analytics.ts`)
 * and Performance Monitoring (`performance.ts`). Initializing the app twice would
 * throw / duplicate SDKs, so both go through `getFirebaseApp()`.
 *
 * Disabled (returns null) when there's no usable web config or in local dev —
 * so every downstream Firebase feature is a safe no-op without config.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { env } from '@/config/env';

let app: FirebaseApp | null = null;
let tried = false;

/** A usable Firebase web config exists (app-level keys) AND not local dev. */
export function firebaseEnabled(): boolean {
  const f = env.firebase;
  return Boolean(f.apiKey && f.projectId && f.appId) && !(env.isDev && env.appEnv === 'development');
}

/** The shared Firebase app, or null when disabled. Memoized (init once). */
export function getFirebaseApp(): FirebaseApp | null {
  if (tried) return app;
  tried = true;
  if (!firebaseEnabled()) return null;
  try {
    // Deliberately DO NOT pass measurementId. Firebase Analytics resolves the
    // measurement id from the backend GA4 data-stream linked to this appId (the
    // Firebase console → Integrations link is the single source of truth). A
    // local measurementId that differs is ignored AND logs a mismatch warning,
    // so we omit it and let the linked stream decide where events go.
    app = initializeApp({
      apiKey: env.firebase.apiKey,
      authDomain: env.firebase.authDomain,
      projectId: env.firebase.projectId,
      storageBucket: env.firebase.storageBucket,
      messagingSenderId: env.firebase.messagingSenderId,
      appId: env.firebase.appId,
    });
  } catch {
    app = null; // never let Firebase init crash app boot
  }
  return app;
}
